import { getInitials } from '@awpmarine/core/utils';
import { Link, createFileRoute } from '@tanstack/react-router';
import { cn } from 'cn';
import { ArrowRightIcon, AwardIcon, GlobeIcon, TargetIcon, TrendingUpIcon } from 'lucide-react';

import { ClientLogos, ContactCta, Eyebrow, Photo } from '@/components/marketing';
import CrewOnDeckImg from '@/images/marketing/crew-on-deck.jpg';
import OfficerOnBridgeImg from '@/images/marketing/officer-on-bridge.jpg';
import { pageHead } from '@/lib/head';
import { languageTag } from '@/lib/locale';
import { ogImage } from '@/lib/open-graph';
import { m } from '@/paraglide/messages';
import { experienceYears, stats } from '@/site-config';

import { team } from '../team/-data';

export const metadata = () => ({ title: m.about_title(), description: m.about_description() });

export const Route = createFileRoute('/about/')({
  head: () => pageHead({ ...metadata(), canonical: '/about', image: ogImage('/about') }),
  component: AboutPage,
});

const phoneAvatars = 5;

// Message functions, read when the page renders so the text follows its locale.
const values = [
  { icon: TargetIcon, title: m.about_value_customer_title, description: m.about_value_customer_text },
  { icon: AwardIcon, title: m.about_value_ethics_title, description: m.about_value_ethics_text },
  { icon: TrendingUpIcon, title: m.about_value_improvement_title, description: m.about_value_improvement_text },
  { icon: GlobeIcon, title: m.about_value_safety_title, description: m.about_value_safety_text },
];

function AboutPage() {
  return (
    <>
      <section className='mx-auto flex w-full max-w-310 flex-wrap items-end gap-x-18 gap-y-8 px-5 pt-14 pb-12 sm:px-8 sm:pt-18'>
        <div className='min-w-0 flex-[1.3_1_520px]'>
          <Eyebrow>{m.about_eyebrow()}</Eyebrow>
          <h1 className='text-[clamp(40px,5vw,64px)] leading-[1.04] font-bold tracking-tight'>{m.about_heading()}</h1>
        </div>
        <p className='text-body min-w-0 flex-[1_1_360px] text-[19px] leading-[1.65]'>{m.about_lead()}</p>
      </section>

      <section className='mx-auto w-full max-w-310 px-5 sm:px-8'>
        <Photo
          src={OfficerOnBridgeImg}
          alt={m.about_hero_image()}
          position='50% 45%'
          priority
          sizes='(min-width: 1240px) 1240px, 100vw'
          className='aspect-21/8 rounded-[28px]'
        />
      </section>

      <section className='bg-navy mt-14 text-white sm:mt-18'>
        <div className='mx-auto grid w-full max-w-310 grid-cols-2 gap-8 px-5 py-12 sm:grid-cols-4 sm:px-8 sm:py-16'>
          {[
            { value: `${experienceYears}`, label: m.about_stat_years() },
            { value: `${stats.inspections.toLocaleString(languageTag())}+`, label: m.about_stat_inspections() },
            { value: `${stats.ports.toLocaleString(languageTag())}+`, label: m.about_stat_ports() },
            { value: `${stats.countries.toLocaleString(languageTag())}+`, label: m.about_stat_countries() },
          ].map((stat) => (
            <div key={stat.label}>
              <p className='text-[34px] font-bold tracking-[-0.02em] sm:text-5xl'>{stat.value}</p>
              <p className='text-on-navy'>{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <ClientLogos label={m.about_working_with()} />

      <section className='mx-auto flex w-full max-w-310 flex-wrap items-center gap-16 px-5 py-14 sm:px-8 sm:py-26'>
        <div className='min-w-0 flex-[1_1_440px]'>
          <Eyebrow>{m.about_vision()}</Eyebrow>
          <blockquote className='mb-7 text-[clamp(26px,2.6vw,34px)] leading-[1.35] font-semibold tracking-[-0.01em]'>
            {m.about_vision_quote()}
          </blockquote>
          <p className='text-body text-[17px] leading-[1.7]'>{m.about_vision_text()}</p>
        </div>
        <Photo
          src={CrewOnDeckImg}
          alt={m.about_vision_image()}
          position='50% 30%'
          className='aspect-16/10 min-w-0 flex-[1_1_440px]'
        />
      </section>

      <section className='bg-mist'>
        <div className='mx-auto w-full max-w-310 px-5 py-14 sm:px-8 sm:py-24'>
          <h2 className='mb-10 text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
            {m.about_values()}
          </h2>
          <div className='grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-5'>
            {values.map((value) => (
              <div key={value.title()} className='flex flex-col gap-3 rounded-[20px] bg-white p-7'>
                <value.icon className='size-6.5 text-brand' />
                <h3 className='mt-2 text-[19px] font-bold'>{value.title()}</h3>
                <p className='text-body leading-relaxed'>{value.description()}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className='mx-auto w-full max-w-310 px-5 pt-14 sm:px-8 sm:pt-24'>
        {/* No bottom line: the contact band below starts with its own. */}
        <div className='border-line flex flex-wrap items-center justify-between gap-8 border-t py-10'>
          <div>
            <div className='mb-5 flex pl-3.5'>
              {team.map((member, index) => (
                <span
                  key={member.name}
                  className={cn(
                    'bg-mist -ml-3.5 flex size-16 items-center justify-center overflow-hidden rounded-full border-3 border-white',
                    // A phone fits five before the row runs off the screen; the count below stands for the rest.
                    index >= phoneAvatars && 'max-sm:hidden',
                  )}
                >
                  {member.photo ? (
                    <img src={member.photo} alt={member.name} loading='lazy' className='size-full object-cover' />
                  ) : (
                    <span className='text-subtle font-semibold'>{getInitials(member.name)}</span>
                  )}
                </span>
              ))}
              {team.length > phoneAvatars && (
                <span className='bg-mist text-subtle -ml-3.5 flex size-16 items-center justify-center rounded-full border-3 border-white font-semibold sm:hidden'>
                  +{team.length - phoneAvatars}
                </span>
              )}
            </div>
            <h2 className='mb-2 text-[28px] font-bold'>{m.about_team_title()}</h2>
            <p className='text-body text-[17px]'>{m.about_team_text()}</p>
          </div>
          <Link
            to='/team'
            className='inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full bg-brand px-7 font-semibold text-white transition-colors hover:bg-[#1f4f8a] disabled:opacity-70'
          >
            {m.about_team_link()} <ArrowRightIcon className='size-4.5' />
          </Link>
        </div>
      </section>

      <ContactCta />
    </>
  );
}
